var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { renderToString } from 'react-dom/server';
import ReactCalendar from 'react-calendar';
import { Icon } from '@gerdau/hefesto/Icon';
import { getDefaultActiveStartDate, isWeekend, resetHoursFromDate, } from '../DateField.utils';
import 'react-calendar/dist/Calendar.css';
import './Calendar.styles.scss';
var Calendar = function (_a) {
    var value = _a.value, onChange = _a.onChange, maxDate = _a.maxDate, minDate = _a.minDate, _b = _a.isRange, isRange = _b === void 0 ? false : _b, disableWeekend = _a.disableWeekend, inputRef = _a.inputRef, _c = _a.picker, picker = _c === void 0 ? 'date' : _c, warningDatesMessage = _a.warningDatesMessage, allowPartialRange = _a.allowPartialRange;
    var calendarRef = useRef(null);
    var getCalendarPositionStyles = function () {
        var _a, _b, _c;
        var bodyClientRect = document.body.getBoundingClientRect();
        var documentBodyHeight = bodyClientRect.height;
        var inputRect = (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        var inputPosition = (inputRect === null || inputRect === void 0 ? void 0 : inputRect.top) || 0;
        var calendarHeight = 288;
        var calendarWidth = 288;
        var right;
        if (inputRect &&
            inputRect.width < calendarWidth &&
            bodyClientRect.width - inputRect.right < calendarWidth &&
            inputRect.right - calendarWidth > 0) {
            right = 0;
        }
        var calendarPositionStyles = {
            top: "".concat(Number((_b = inputRef.current) === null || _b === void 0 ? void 0 : _b.clientHeight) / 16, "rem"),
        };
        if (inputPosition + calendarHeight >= documentBodyHeight &&
            inputPosition > calendarHeight) {
            calendarPositionStyles = {
                bottom: "".concat(Number((_c = inputRef.current) === null || _c === void 0 ? void 0 : _c.clientHeight) / 16, "rem"),
            };
        }
        return __assign(__assign({}, calendarPositionStyles), { right: right });
    };
    var defaultActiveStartDate = getDefaultActiveStartDate(isRange, value, maxDate);
    var _d = useState(false), needToShowInfoBlock = _d[0], setNeedToShowInfoBlock = _d[1];
    var _e = useState(false), isShowingInfoBlock = _e[0], setIsShowingInfoBlock = _e[1];
    var addInfoBlock = function () {
        var _a;
        if (!warningDatesMessage) {
            return;
        }
        var calendarContainer = (_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.react-calendar');
        var infoBlockString = renderToString(_jsx(InfoBlock, { message: warningDatesMessage.infoMessage }));
        var div = document.createElement('div');
        div.innerHTML = infoBlockString.trim();
        calendarContainer === null || calendarContainer === void 0 ? void 0 : calendarContainer.appendChild(div.firstChild);
        setIsShowingInfoBlock(true);
    };
    var removeInfoBlock = function () {
        var _a;
        var calendarContainer = (_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('.react-calendar');
        var infoBlock = calendarContainer === null || calendarContainer === void 0 ? void 0 : calendarContainer.querySelector('.hefesto-datefield-calendar__info');
        infoBlock === null || infoBlock === void 0 ? void 0 : infoBlock.classList.add('remove');
        setTimeout(function () {
            infoBlock === null || infoBlock === void 0 ? void 0 : infoBlock.remove();
        }, 200);
        setIsShowingInfoBlock(false);
        setNeedToShowInfoBlock(false);
    };
    useEffect(function () {
        if (needToShowInfoBlock && !isShowingInfoBlock) {
            addInfoBlock();
        }
    });
    return (_jsx("div", __assign({ className: "hefesto-datefield-calendar", "data-testid": "hefesto-datefield-calendar", style: getCalendarPositionStyles(), ref: calendarRef }, { children: _jsx(ReactCalendar, { locale: "pt-br", prev2Label: null, next2Label: null, selectRange: isRange, navigationLabel: function (_a) {
                var date = _a.date;
                var monthName = date.toLocaleString('pt-br', { month: 'long' });
                return "".concat(monthName, " ").concat(date.getFullYear());
            }, maxDate: maxDate, minDate: minDate, defaultActiveStartDate: defaultActiveStartDate, onActiveStartDateChange: function (props) {
                var _a;
                return props.view === 'month' &&
                    warningDatesMessage &&
                    isShowingInfoBlock &&
                    ((_a = calendarRef.current) === null || _a === void 0 ? void 0 : _a.getElementsByClassName('info-date-tile').length) === 0 &&
                    removeInfoBlock();
            }, onDrillUp: function () { return isShowingInfoBlock && removeInfoBlock(); }, prevLabel: _jsx("div", __assign({ className: "calendar__icon_wrapper" }, { children: _jsx(Icon, { option: "ChevronLeft", variant: "primary" }) })), nextLabel: _jsx("div", __assign({ className: "calendar__icon_wrapper" }, { children: _jsx(Icon, { option: "ChevronRight", variant: "primary" }) })), value: value, onChange: onChange, maxDetail: picker === 'month' ? 'year' : undefined, tileClassName: function (tileProps) {
                var tileClassName = '';
                if (warningDatesMessage && tileProps.view === 'month') {
                    var tileDateTime = tileProps.date.getTime();
                    var startDateTime = resetHoursFromDate(warningDatesMessage.startDate).getTime();
                    var endDateTime = resetHoursFromDate(warningDatesMessage.endDate).getTime();
                    if (tileDateTime >= startDateTime && tileDateTime <= endDateTime) {
                        setNeedToShowInfoBlock(true);
                        tileClassName = 'info-date-tile';
                    }
                }
                return tileClassName;
            }, tileDisabled: function (_a) {
                var date = _a.date, view = _a.view;
                return disableWeekend && isWeekend(date) && view === 'month';
            }, allowPartialRange: allowPartialRange }) })));
};
var InfoBlock = function (_a) {
    var message = _a.message;
    return (_jsxs("div", __assign({ className: "hefesto-datefield-calendar__info" }, { children: [_jsx(Icon, { option: "Alert", variant: "warning" }), _jsx("span", __assign({ className: "hefesto-datefield-calendar__info__description" }, { children: message }))] })));
};
export default Calendar;
